import { Lock, ArrowUpCircle, Sparkles, Crown } from "lucide-react";

// Apenas 2 planos vigentes (espelham a landing): Start e Completo.
// Os slugs antigos (starter, professional, enterprise) ficam como ALIASES
// nivel "completo" pra não quebrar tenants com plano legado.
const PLAN_HIERARCHY = {
  start: 0,
  completo: 1,
  // legacy aliases — todos viram nível Completo
  starter: 1,
  professional: 1,
  enterprise: 1,
};

const PLAN_LABELS = {
  start: "Start",
  completo: "Completo",
  // legacy aliases
  starter: "Completo",
  professional: "Completo",
  enterprise: "Completo",
};

// Visual: Start = teal/sparkles, Completo = laranja/coroa
export const PLAN_VISUAL = {
  start: { icon: Sparkles, color: "#2A9D8F", bg: "bg-teal-500/15", text: "text-teal-400", border: "border-teal-500/30" },
  completo: { icon: Crown, color: "#FF6600", bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  // legacy aliases — mesmo visual do Completo
  starter: { icon: Crown, color: "#FF6600", bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  professional: { icon: Crown, color: "#FF6600", bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  enterprise: { icon: Crown, color: "#FF6600", bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
};

// Paths que exigem o plano Completo. Tudo que NÃO está aqui é acessível a todos (Start incluso).
// Baseado no catálogo da landing (start: false em data/plans):
//   Módulo balcão/totem, KDS, Gestor de estoque, Gestão financeira,
//   Registro de compras, Clube de ofertas, Controle de fornecedores, DRE.
const COMPLETO_PATHS = [
  "/kds",
  "/balcao",
  "/totem",
  "/estoque",
  "/estoque/relatorio",
  "/compras",
  "/clientes-fornecedores/fornecedores",
  "/financeiro/extrato",
  "/financeiro/despesas",
  "/impulsione/relatorio-ofertas",
];

// Top menu items que requerem plano (header do app)
const TOP_MENU_PLANS = {
  balcao: "completo",
};

// Módulos do sidebar que requerem plano (badge no header do módulo)
const MODULE_PLANS = {};

// Normaliza qualquer slug legacy pra um dos 2 planos vigentes
function _normalizePlan(plan) {
  if (!plan) return "start";
  if (plan === "start" || plan === "completo") return plan;
  // legacy aliases → tratamos como Completo (têm acesso a tudo)
  return "completo";
}

export function getRequiredPlan(path) {
  if (COMPLETO_PATHS.some((p) => path === p || path.startsWith(p + "/")))
    return "completo";
  return "start";
}

export function getTopMenuRequiredPlan(menuId) {
  return TOP_MENU_PLANS[menuId] || "start";
}

export function getModuleRequiredPlan(moduleKey) {
  return MODULE_PLANS[moduleKey] || "start";
}

export function hasAccess(userPlan, requiredPlan) {
  return (
    (PLAN_HIERARCHY[userPlan] ?? 0) >= (PLAN_HIERARCHY[requiredPlan] ?? 0)
  );
}

export function getPlanLabel(plan) {
  return PLAN_LABELS[plan] || plan;
}

// Mini badge no item bloqueado mostrando o plano necessário (só pra completo)
export function PlanBadge({ requiredPlan, className = "" }) {
  if (!requiredPlan || requiredPlan === "start") return null;
  const norm = _normalizePlan(requiredPlan);
  const visual = PLAN_VISUAL[norm] || PLAN_VISUAL.completo;
  const Icon = visual.icon;
  return (
    <span
      title={`Disponível no plano ${getPlanLabel(norm)}`}
      className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-md ${visual.bg} ${visual.text} ${className}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {getPlanLabel(norm)}
    </span>
  );
}

// Helper para ordenar items do sidebar: liberados primeiro, bloqueados no final.
export function sortByLocked(items, userPlan) {
  return [...(items || [])].sort((a, b) => {
    const la = a.requiredPlan && !hasAccess(userPlan, a.requiredPlan) ? 1 : 0;
    const lb = b.requiredPlan && !hasAccess(userPlan, b.requiredPlan) ? 1 : 0;
    return la - lb;
  });
}

// Verifica o estado de um módulo (header + filhos) para o plano do usuário
export function getModuleLockState(children, userPlan) {
  const list = (children || []).filter(c => c && c.path);
  if (list.length === 0) return { fullyLocked: false, requiredPlan: null };
  const accessible = list.some(c => !c.requiredPlan || hasAccess(userPlan, c.requiredPlan));
  if (accessible) return { fullyLocked: false, requiredPlan: null };
  // Todos bloqueados — pega o menor (mais barato) entre os requiredPlan dos filhos
  const plans = list.map(c => c.requiredPlan).filter(Boolean);
  const cheapest = plans.sort(
    (a, b) => (PLAN_HIERARCHY[a] ?? 99) - (PLAN_HIERARCHY[b] ?? 99)
  )[0];
  return { fullyLocked: true, requiredPlan: cheapest };
}

// Overlay de blur para páginas bloqueadas
export function PlanGateOverlay({ requiredPlan, children }) {
  const planName = getPlanLabel(_normalizePlan(requiredPlan));
  return (
    <div className="relative w-full h-full min-h-[80vh]">
      <div className="filter blur-[6px] pointer-events-none select-none opacity-60">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-20">
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">
            Recurso do plano {planName}
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Para acessar este recurso, faça upgrade para o plano{" "}
            <strong>{planName}</strong>.
          </p>
          <a
            href="https://usenucleo.com.br/#planos"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
          >
            <ArrowUpCircle className="w-4 h-4" />
            Ver planos
          </a>
        </div>
      </div>
    </div>
  );
}
